import { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import type { User } from "components/userModel";
import { getJaDisplayName } from "components/userUtil";
import { updateUserDetail } from "./updateUserDetail";

interface Props {
  user: User,
  refresh: () => void,
}

export default function UserEditComponent({ user: initialUser, refresh }: Props) {
  const [user, setUser] = useState<User>(initialUser);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Toast state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Handle update button click
  const handleUpdate = async () => {
    if (!window.confirm("会員情報を更新しますか？")) {
      return;
    }

    setIsLoading(true);
    try {
      await updateUserDetail({
        userId: initialUser.id,
        applicantName: user.applicantName,
        applicantNameKana: user.applicantNameKana,
        birthDate: user.birthDate,
        age: user.age,
        homePostalCode: user.homePostalCode,
        homePrefecture: user.homePrefecture,
        homeCity: user.homeCity,
        homeAddress1: user.homeAddress1,
        homeAddress2: user.homeAddress2,
        homePhoneNumber: user.homePhoneNumber,
        mobileNumber: user.mobileNumber,
        companyName: user.companyName,
        invoiceNumber: user.invoiceNumber,
        corporateNumber: user.corporateNumber,
        postalCode: user.postalCode,
        prefecture: user.prefecture,
        city: user.city,
        address1: user.address1,
        address2: user.address2,
        phoneNumber: user.phoneNumber,
        faxNumber: user.faxNumber,
        industryType: user.industryType,
        annualSales: user.annualSales,
        employeeCount: user.employeeCount,
        taxDelayType: initialUser.taxDelayType,
        taxDelayBalance: initialUser.taxDelayBalance,
        taxDelayStatus: initialUser.taxDelayStatus,
        familyStructure: user.familyStructure ?? [],
        factoringHistory: user.factoringHistory,
        pastProviders: user.pastProviders,
        pastProviderAmount: user.pastProviderAmount,
        currentProviderHistory: user.currentProviderHistory,
        currentProviders: user.currentProviders,
        currentProviderAmount: user.currentProviderAmount,
        isApproved: user.isApproved,
      });

      refresh();
      setShowSuccessToast(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "会員情報の更新中にエラーが発生しました。"
      );
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast close handlers
  const handleCloseSuccessToast = () => {
    setShowSuccessToast(false);
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  const updateStringField = (field: string, value: string) => {
    setUser(prev => ({ ...prev, [field]: value }));
  };

  const updateNumberField = (field: string, value: string) => {
    const num = value === '' ? 0 : Number(value);
    setUser(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          会員詳細
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {/* Basic Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("memberNumber")}
              name="memberNumber"
              value={user.memberNumber}
              onChange={(e) => updateStringField('memberNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("companyName")}
              name="companyName"
              value={user.companyName}
              onChange={(e) => updateStringField('companyName', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("applicantName")}
              name="applicantName"
              value={user.applicantName}
              onChange={(e) => updateStringField('applicantName', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("applicantNameKana")}
              name="applicantNameKana"
              value={user.applicantNameKana}
              onChange={(e) => updateStringField('applicantNameKana', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("birthDate")}
              name="birthDate"
              type="date"
              value={user.birthDate}
              onChange={(e) => updateStringField('birthDate', e.target.value)}
              margin="normal"
              disabled={isLoading}
              placeholder="YYYY-MM-DD"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  min: "1900-01-01",
                  max: "9999-12-31"
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={getJaDisplayName("age")}
              name="age"
              value={user.age.toString()}
              onChange={(e) => updateNumberField('age', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("phoneNumber")}
              name="phoneNumber"
              value={user.phoneNumber}
              onChange={(e) => updateStringField('phoneNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("faxNumber")}
              name="faxNumber"
              value={user.faxNumber}
              onChange={(e) => updateStringField('faxNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("mobileNumber")}
              name="mobileNumber"
              value={user.mobileNumber}
              onChange={(e) => updateStringField('mobileNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("invoiceNumber")}
              name="invoiceNumber"
              value={user.invoiceNumber}
              onChange={(e) => updateStringField('invoiceNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("corporateNumber")}
              name="corporateNumber"
              value={user.corporateNumber}
              onChange={(e) => updateStringField('corporateNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>

          {/* Home Address Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homePostalCode")}
              name="homePostalCode"
              value={user.homePostalCode}
              onChange={(e) => updateStringField('homePostalCode', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homePrefecture")}
              name="homePrefecture"
              value={user.homePrefecture}
              onChange={(e) => updateStringField('homePrefecture', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homeCity")}
              name="homeCity"
              value={user.homeCity}
              onChange={(e) => updateStringField('homeCity', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homeAddress1")}
              name="homeAddress1"
              value={user.homeAddress1}
              onChange={(e) => updateStringField('homeAddress1', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homeAddress2")}
              name="homeAddress2"
              value={user.homeAddress2}
              onChange={(e) => updateStringField('homeAddress2', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("homePhoneNumber")}
              name="homePhoneNumber"
              value={user.homePhoneNumber}
              onChange={(e) => updateStringField('homePhoneNumber', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>

          {/* Company Address Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("postalCode")}
              name="postalCode"
              value={user.postalCode}
              onChange={(e) => updateStringField('postalCode', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("prefecture")}
              name="prefecture"
              value={user.prefecture}
              onChange={(e) => updateStringField('prefecture', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("city")}
              name="city"
              value={user.city}
              onChange={(e) => updateStringField('city', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("address1")}
              name="address1"
              value={user.address1}
              onChange={(e) => updateStringField('address1', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("address2")}
              name="address2"
              value={user.address2}
              onChange={(e) => updateStringField('address2', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>

          {/* Business Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("industryType")}
              name="industryType"
              value={user.industryType}
              onChange={(e) => updateStringField('industryType', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("annualSales")}
              name="annualSales"
              value={user.annualSales}
              onChange={(e) => updateStringField('annualSales', e.target.value)}
              margin="normal"
              disabled={isLoading}
              slotProps={{
                input: {
                  endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>円</Typography>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("employeeCount")}
              name="employeeCount"
              value={user.employeeCount}
              onChange={(e) => updateStringField('employeeCount', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>

          {/* Factoring History */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("factoringHistory")}
              name="factoringHistory"
              value={user.factoringHistory}
              onChange={(e) => updateStringField('factoringHistory', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("pastProviders")}
              name="pastProviders"
              value={user.pastProviders}
              onChange={(e) => updateStringField('pastProviders', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("pastProviderAmount")}
              name="pastProviderAmount"
              value={user.pastProviderAmount.toString()}
              onChange={(e) => updateNumberField('pastProviderAmount', e.target.value)}
              margin="normal"
              disabled={isLoading}
              slotProps={{
                input: {
                  endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                },
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 0,
                  max: 9999999999
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("currentProviderHistory")}
              name="currentProviderHistory"
              value={user.currentProviderHistory}
              onChange={(e) => updateStringField('currentProviderHistory', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("currentProviders")}
              name="currentProviders"
              value={user.currentProviders}
              onChange={(e) => updateStringField('currentProviders', e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("currentProviderAmount")}
              name="currentProviderAmount"
              value={user.currentProviderAmount.toString()}
              onChange={(e) => updateNumberField('currentProviderAmount', e.target.value)}
              margin="normal"
              disabled={isLoading}
              slotProps={{
                input: {
                  endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                },
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 0,
                  max: 9999999999
                },
              }}
            />
          </Grid>

          {/* Family Structure */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {getJaDisplayName("familyStructure")}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={isLoading}
                onClick={() => {
                  setUser(prev => ({
                    ...prev,
                    familyStructure: [
                      ...(prev.familyStructure ?? []),
                      { relationship: "", fullName: "", age: 0, contactInfo: "" }
                    ],
                  }));
                }}
              >
                家族を追加
              </Button>
            </Box>
            {(user.familyStructure && user.familyStructure.length > 0) ? (
              <Grid container spacing={2}>
                {user.familyStructure.map((fam, idx) => (
                  <Grid key={idx} size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 3 }}>
                          <TextField
                            fullWidth
                            label={getJaDisplayName("relationship")}
                            value={fam.relationship}
                            onChange={(e) => {
                              const value = e.target.value;
                              setUser(prev => ({
                                ...prev,
                                familyStructure: (prev.familyStructure ?? []).map((f, i) => i === idx ? { ...f, relationship: value } : f)
                              }));
                            }}
                            margin="normal"
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <TextField
                            fullWidth
                            label={getJaDisplayName("fullName")}
                            value={fam.fullName}
                            onChange={(e) => {
                              const value = e.target.value;
                              setUser(prev => ({
                                ...prev,
                                familyStructure: (prev.familyStructure ?? []).map((f, i) => i === idx ? { ...f, fullName: value } : f)
                              }));
                            }}
                            margin="normal"
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label={getJaDisplayName("age")}
                            value={fam.age.toString()}
                            onChange={(e) => {
                              const num = e.target.value === '' ? 0 : Number(e.target.value);
                              const value = isNaN(num) ? 0 : num;
                              setUser(prev => ({
                                ...prev,
                                familyStructure: (prev.familyStructure ?? []).map((f, i) => i === idx ? { ...f, age: value } : f)
                              }));
                            }}
                            margin="normal"
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <TextField
                            fullWidth
                            label={getJaDisplayName("contactInfo")}
                            value={fam.contactInfo}
                            onChange={(e) => {
                              const value = e.target.value;
                              setUser(prev => ({
                                ...prev,
                                familyStructure: (prev.familyStructure ?? []).map((f, i) => i === idx ? { ...f, contactInfo: value } : f)
                              }));
                            }}
                            margin="normal"
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 1 }}>
                          <Button
                            variant="text"
                            color="error"
                            disabled={isLoading}
                            onClick={() => {
                              setUser(prev => ({
                                ...prev,
                                familyStructure: (prev.familyStructure ?? []).filter((_, i) => i !== idx)
                              }));
                            }}
                          >
                            削除
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                家族構成の登録はありません。
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "更新中..." : "更新"}
          </Button>
        </Box>
      </Paper>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseSuccessToast}
      >
        <Alert
          onClose={handleCloseSuccessToast}
          severity="success"
          sx={{ width: "100%" }}
        >
          会員情報を正常に更新しました。
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
      >
        <Alert
          onClose={handleCloseErrorToast}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
